import { useCallback, useRef, useState } from 'react';
import { StreamChat, OwnUserResponse, StreamChatOptions, TokenOrProvider, UserResponse } from 'stream-chat';
import { useEffectOnce } from 'react-use';
import { installStreamSentryInterceptors } from '@/getStreamLogger';

type UseCreateStreamChatProps = {
  apiKey: string;
  tokenOrProvider: TokenOrProvider;
  userData: OwnUserResponse | UserResponse;
  options?: StreamChatOptions;
};

type UseCreateStreamChatType = {
  chatClient: StreamChat | null;
  connectToChat: () => void;
  isConnecting: boolean;
  isConnected: boolean;
  isConnectionError: boolean;
};

export const useCreateStreamChat = ({
  apiKey,
  options,
  tokenOrProvider,
  userData,
}: UseCreateStreamChatProps): UseCreateStreamChatType => {
  const [isConnecting, setConnecting] = useState(false);
  const [isConnected, setConnected] = useState(false);
  const [isConnectionError, setConnectionError] = useState(false);
  const [cachedUserData, setCachedUserData] = useState(userData);
  const chatClientRef = useRef<StreamChat | null>(null);

  if (userData.id !== cachedUserData.id) {
    setCachedUserData(userData);
  }

  const { current: cachedOptions } = useRef(options);

  const disconnectChat = useCallback(async () => {
    if (!chatClientRef.current) return;
    try {
      await chatClientRef.current.disconnectUser();

      setConnected(false);
      setConnecting(false);
      setConnectionError(false);
      console.log(`Connection for user "${cachedUserData.id}" has been closed`);
    } catch (e) {
      console.error(e);
    }
  }, [cachedUserData.id]);

  const connectToChat = useCallback(async () => {
    try {
      await disconnectChat();

      setConnecting(true);

      let client = chatClientRef.current;

      if (!client) {
        client = new StreamChat(apiKey, undefined, cachedOptions);

        chatClientRef.current = client;

        installStreamSentryInterceptors(client);
      }

      await client.connectUser(cachedUserData, tokenOrProvider);
      setConnected(true);
    } catch (e) {
      setConnectionError(true);
      console.error(e);
    } finally {
      setConnecting(false);
    }
  }, [disconnectChat, cachedUserData, tokenOrProvider, apiKey, cachedOptions]);

  useEffectOnce(() => {
    connectToChat();

    return () => {
      disconnectChat();
    };
  });

  return {
    chatClient: chatClientRef.current,
    connectToChat,
    isConnecting,
    isConnected,
    isConnectionError,
  };
};
